import { App as AntdApp, theme as antdTheme, ConfigProvider } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'boss-admin-theme-mode';

const ThemeModeContext = createContext(null);

function readPersistedMode() {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'light';
}

function readSystemPrefersDark() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveMode(mode, systemPrefersDark) {
    if (mode === 'system') return systemPrefersDark ? 'dark' : 'light';
    return mode;
}

export function ThemeProvider({ children }) {
    const [mode, setModeState] = useState(readPersistedMode);
    const [systemPrefersDark, setSystemPrefersDark] = useState(readSystemPrefersDark);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = event => setSystemPrefersDark(event.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    const setMode = useCallback(next => {
        setModeState(next);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, next);
        }
    }, []);

    const resolvedMode = resolveMode(mode, systemPrefersDark);

    const toggleMode = useCallback(() => {
        setMode(resolvedMode === 'dark' ? 'light' : 'dark');
    }, [resolvedMode, setMode]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        root.dataset.themeMode = resolvedMode;
        root.style.colorScheme = resolvedMode;
    }, [resolvedMode]);

    const value = useMemo(() => ({ mode, resolvedMode, setMode, toggleMode }), [mode, resolvedMode, setMode, toggleMode]);

    const isDark = resolvedMode === 'dark';

    return (
        <ThemeModeContext.Provider value={value}>
            <ConfigProvider
                locale={zhCN}
                theme={{
                    cssVar: { key: 'boss-admin' },
                    hashed: false,
                    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                }}
            >
                <AntdApp>{children}</AntdApp>
            </ConfigProvider>
        </ThemeModeContext.Provider>
    );
}

export function useThemeMode() {
    const ctx = useContext(ThemeModeContext);
    if (ctx == null) {
        throw new Error('useThemeMode 必须在 ThemeProvider 内使用');
    }
    return ctx;
}
